import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../components/ui/form";
import { useScrollSpy } from "../../../../hooks/useScrollSpy";
import { getErrorMessage } from "../../../../lib/query/errors";
import { useSaveStatusStore } from "../../../../stores/saveStatusStore";
import { useReadOnly } from "../../../lock/ReadOnlyContext";
import type { ScrollSpySection } from "../../../stipulations/stipulationCategoryFilters";
import { useStipulationCategoryStore } from "../../../stipulations/stipulationCategoryStore";
import Section from "../../components/Section";
import { useSaveSignerApplicant } from "./api/useSaveSignerApplicant";
import { SignerColumn } from "./fields/SignerColumn";
import { ReadOnlyProvider } from "./readOnlyContext";
import { useSignersForm } from "./useSignersForm";

// ---------------------------------------------------------------------------
// Signers card — collapsible outer card (via Section/Accordion) with a
// two-column Driver | 2nd Signer layout on desktop, stacked on mobile.
//
// Visibility is fully driven by the DSC Workbench dashboard response: the
// Driver column renders only when `driver.present` is true, and the
// 2nd Signer column only when `secondSigner.present` is true. There is
// no add/remove-2nd-signer flow — the response is the single source of truth
// for who appears on the deal.
//
// Form VALUES are still local-only (BFF income hydration is deferred). Each
// present signer seeds exactly ONE blank income block on mount; totals show
// $0.00 until income hydration is added in a future ticket. The GET response
// (step6-bff-client 1.0.171) no longer carries income or employment data.
// ---------------------------------------------------------------------------
// Splits a camelCase field name into words for a human-readable label
// (e.g. "employerName" -> "Employer Name"), so validation-failure banners
// name the actual offending field instead of guessing.
function humanizeFieldName(name: string): string {
  const spaced = name.replaceAll(/([\da-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Walks a react-hook-form FieldErrors tree and collects the (deduped, human-
// readable) names of every leaf field that failed validation. A node is a
// leaf when it carries RHF's own `type`/`message` (its nested-object shape is
// otherwise indistinguishable from a plain sub-schema object).
function collectInvalidFieldNames(node: unknown, results: Set<string> = new Set()): Set<string> {
  if (node == null || typeof node !== "object") {
    return results;
  }
  const record = node as Record<string, unknown>;
  if (typeof record.type === "string" && typeof record.message === "string") {
    return results;
  }
  for (const [key, value] of Object.entries(record)) {
    if (key === "ref") {
      continue;
    }
    if (value && typeof value === "object") {
      const child = value as Record<string, unknown>;
      if (typeof child.type === "string" && typeof child.message === "string") {
        results.add(humanizeFieldName(key));
      } else {
        collectInvalidFieldNames(value, results);
      }
    }
  }
  return results;
}

interface SignersProperties {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
  /** Deal / approval id, threaded down to each income block's employer-info flow. */
  approvalId?: string;
}

/** Imperative handle exposed to ReviewPage so it can submit this card's form without reaching into its DOM. */
export interface SignersHandle {
  /**
   * Submits the form and resolves once the save settles. Resolves `true` on a
   * successful save (warnings still surface as banners but aren't a failure),
   * `false` on a validation failure or a save error — callers like the Exit
   * modal's Save & Exit use this to decide whether to proceed with the exit.
   */
  requestSubmit: () => Promise<boolean>;
}

const Signers = forwardRef<SignersHandle, SignersProperties>(function Signers(
  { driver, secondSigner, approvalId },
  reference
) {
  const { t } = useTranslation();
  const formReference = useRef<HTMLFormElement>(null);
  // Set inside the handleSubmit valid/invalid callbacks below; read by the
  // imperative handle once the submit's promise settles.
  const lastSubmitSucceededReference = useRef(false);
  useImperativeHandle(reference, () => ({
    requestSubmit: async () => {
      await onSubmit();
      return lastSubmitSucceededReference.current;
    },
  }));
  const readOnly = useReadOnly();
  const showDriver = Boolean(driver?.present);
  const showSecondSigner = Boolean(secondSigner?.present);

  const { form, control, driverIncomes, secondSignerIncomes, driverSsnType, secondSignerSsnType } = useSignersForm({
    driver,
    secondSigner,
  });

  const saveSignerApplicant = useSaveSignerApplicant();
  const [driverError, setDriverError] = useState<string>();
  const [secondSignerError, setSecondSignerError] = useState<string>();

  // FooterAction's Save button lives outside this form (as a ReviewPage
  // sibling), so its disabled-while-pending state has to travel through a
  // store rather than props.
  const setIsSaving = useSaveStatusStore((state) => state.setIsSaving);
  useEffect(() => {
    setIsSaving(saveSignerApplicant.isPending);
  }, [saveSignerApplicant.isPending, setIsSaving]);

  const onSubmit = form.handleSubmit(
    async (values) => {
      setDriverError(undefined);
      setSecondSignerError(undefined);

      if (!approvalId) {
        lastSubmitSucceededReference.current = false;
        return;
      }

      try {
        const { warnings } = await saveSignerApplicant.mutateAsync({
          approvalId,
          driver: values.driver,
          secondSigner: values.secondSigner,
        });
        for (const warning of warnings) {
          if (warning.section === "second-signer") {
            setSecondSignerError(warning.message);
          } else if (warning.section === "signer") {
            setDriverError(warning.message);
          }
        }
        lastSubmitSucceededReference.current = true;
      } catch (error) {
        setDriverError(t("signers.saveError", { message: getErrorMessage(error) }));
        lastSubmitSucceededReference.current = false;
      }
    },
    // Zod validation failed — Save never reaches the BFF client. Surface a
    // banner per affected column naming the actual invalid field(s), so this
    // doesn't fail silently (or misleadingly blame an unrelated field).
    (errors) => {
      setDriverError(
        errors.driver
          ? t("signers.validationError", { fields: [...collectInvalidFieldNames(errors.driver)].join(", ") })
          : undefined
      );
      setSecondSignerError(
        errors.secondSigner
          ? t("signers.validationError", { fields: [...collectInvalidFieldNames(errors.secondSigner)].join(", ") })
          : undefined
      );
      lastSubmitSucceededReference.current = false;
    }
  );

  // Real applicant ids for the Work Number flow, sourced straight from the
  // dashboard response (driver/secondSigner.applicantId).
  const driverApplicantId = driver?.applicantId;
  const secondSignerApplicantId = secondSigner?.applicantId;

  // Scroll-spy: as the analyst scrolls past each Income / Bank / Residence
  // subsection (in either column), drive the Stipulations panel's category
  // filter to match. Called unconditionally — before the early return below —
  // per the rules of hooks.
  const setStipulationCategory = useStipulationCategoryStore((state) => state.setCategory);
  const registerSection = useScrollSpy<ScrollSpySection>(setStipulationCategory);

  // Nothing to render until the response includes at least one present signer.
  if (!showDriver && !showSecondSigner) {
    return null;
  }

  return (
    <Section title={t("signers.sectionTitle")}>
      <Form {...form}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form ref={formReference} onSubmit={onSubmit} noValidate>
          {/* A disabled fieldset disables every nested *native* control, so the
              whole Signers form goes read-only under the VLA lock. Radix Select
              triggers and the tel: call anchor ignore the fieldset, so they read
              the same flag from ReadOnlyProvider instead. */}
          <ReadOnlyProvider value={readOnly}>
            <fieldset disabled={readOnly} className="m-0 w-full min-w-0 border-0 p-0">
              {/* Two-column layout: Driver | 2nd Signer */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {showDriver && (
                  <SignerColumn
                    title={t("signers.driver")}
                    personPath="driver"
                    control={control}
                    fields={driverIncomes.fields}
                    append={driverIncomes.append}
                    remove={driverIncomes.remove}
                    approvalId={approvalId}
                    applicantId={driverApplicantId}
                    applicantSsnType={driverSsnType}
                    registerSection={registerSection}
                    errorMessage={driverError}
                  />
                )}

                {showSecondSigner && (
                  // When both columns render, separate them with a divider:
                  // horizontal above the 2nd Signer when stacked, vertical between
                  // the columns at lg. No divider when the 2nd Signer stands alone.
                  <div
                    className={
                      showDriver ? "border-border border-t pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0" : undefined
                    }
                  >
                    <SignerColumn
                      title={t("signers.secondSigner")}
                      personPath="secondSigner"
                      control={control}
                      fields={secondSignerIncomes.fields}
                      append={secondSignerIncomes.append}
                      remove={secondSignerIncomes.remove}
                      approvalId={approvalId}
                      applicantId={secondSignerApplicantId}
                      applicantSsnType={secondSignerSsnType}
                      registerSection={registerSection}
                      errorMessage={secondSignerError}
                    />
                  </div>
                )}
              </div>
            </fieldset>
          </ReadOnlyProvider>
        </form>
      </Form>
    </Section>
  );
});

export default Signers;
